import { useState, useEffect, useCallback } from "react";
import { apiFetch } from "../../../context/AuthContext";

const BASE_URL = process.env.REACT_APP_BASE_URL;

const EMPTY_FORM = {
    sr: "",
    email: "",
    clientName: "",
    infinityCode: "",
    phoneNumber: "",
    location: "",
    password: "",
};

// `t` is the page copy (constants/adminAccountText.js) so every message
// follows the current language. Results show in the shared AlertModal.
const useAdminAccountCreation = (t) => {
    const [companies, setCompanies] = useState([]);
    const [loadingCompanies, setLoadingCompanies] = useState(false);
    const [saving, setSaving] = useState(false);
    const [alert, setAlert] = useState({ open: false, title: "", message: "", variant: "info" });
    const [form, setForm] = useState(EMPTY_FORM);

    const showMessage = useCallback(
        (type, message) => {
            setAlert({ open: true, title: type === "error" ? t.error : t.success, message, variant: type });
        },
        [t]
    );
    const closeAlert = () => setAlert((a) => ({ ...a, open: false }));

    useEffect(() => {
        const fetchCompanies = async () => {
            try {
                setLoadingCompanies(true);
                const response = await apiFetch(`${BASE_URL}/api/configurations/companies`, { method: "GET" });
                const data = await response.json();
                const list = data?.data || [];
                setCompanies(list);
                if (list.length > 0) {
                    setForm((prev) => (prev.clientName ? prev : { ...prev, clientName: list[0].clientName }));
                }
            } catch (error) {
                console.error("Failed to load companies:", error);
                showMessage("error", t.loadCompaniesFailed);
            } finally {
                setLoadingCompanies(false);
            }
        };
        fetchCompanies();
        // Load once; a language switch must not refetch.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onChange = (field, value) => {
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const resetForm = () => setForm((prev) => ({ ...EMPTY_FORM, clientName: prev.clientName }));

    const validate = () => {
        const problem = !form.sr.trim()
            ? t.nameRequired
            : !form.email.trim()
            ? t.emailRequired
            : !form.clientName.trim()
            ? t.companyRequired
            : !form.password.trim()
            ? t.passwordRequired
            : form.password.trim().length < 4
            ? t.passwordShort
            : null;
        if (problem) showMessage("error", problem);
        return !problem;
    };

    const handleCreateAdmin = async (e) => {
        e?.preventDefault?.();
        if (saving || !validate()) return;

        try {
            setSaving(true);

            const payload = {
                sr: form.sr.trim(),
                email: form.email.trim(),
                clientName: form.clientName.trim(),
                employeeState: "Active",
                password: form.password,
                loginCount: 2,
            };

            if (form.infinityCode.trim()) payload.infinityCode = form.infinityCode.trim();
            if (form.phoneNumber.trim()) payload.phoneNumber = form.phoneNumber.trim();
            if (form.location.trim()) payload.location = form.location.trim();

            const createResponse = await apiFetch(`${BASE_URL}/employees`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(payload),
            });

            if (!createResponse.ok) {
                const errorText = await createResponse.text();
                throw new Error(errorText || t.profileFailed);
            }

            const createJson = await createResponse.json();
            const createdInfinityCode =
                createJson?.employee?.infinityCode || createJson?.data?.infinityCode || payload.infinityCode;

            if (!createdInfinityCode) {
                throw new Error(t.codeMissing);
            }

            const promoteResponse = await apiFetch(`${BASE_URL}/employees/make-admin`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    infinityCodes: [createdInfinityCode],
                    clientName: form.clientName.trim(),
                }),
            });

            if (!promoteResponse.ok) {
                const errorText = await promoteResponse.text();
                throw new Error(errorText || t.promoteFailed);
            }

            showMessage("success", t.created(form.email.trim()));
            resetForm();
        } catch (error) {
            console.error("Failed to create admin account:", error);
            showMessage("error", error.message || t.createFailed);
        } finally {
            setSaving(false);
        }
    };

    return {
        companies,
        loadingCompanies,
        saving,
        alert,
        closeAlert,
        form,
        onChange,
        resetForm,
        handleCreateAdmin,
    };
};

export default useAdminAccountCreation;
